import { Point, Stroke, RecordingEvent } from '../types';

/**
 * Coordinate normalization system for cross-device compatibility
 * All coordinates are normalized to 0-1 range relative to canvas dimensions
 */

export interface NormalizedPoint {
  x: number; // 0-1 range
  y: number; // 0-1 range
  pressure?: number;
  tiltX?: number;
  tiltY?: number;
  timestamp?: number;
}

export interface NormalizedStroke {
  id: string;
  points: NormalizedPoint[];
  color: string;
  width: number; // Normalized relative to canvas width
  size?: number; // Normalized relative to canvas width
  opacity?: number;
  tool: Stroke['tool'];
  timestamp: number;
  isRecognizedShape?: boolean;
  originalShape?: Stroke['originalShape'];
  confidence?: number;
  transform?: {
    left: number; // 0-1 range
    top: number; // 0-1 range
    scaleX: number;
    scaleY: number;
    angle: number;
  };
  _isNormalized?: boolean; // Flag to identify normalized strokes
}

/**
 * Normalize a single point relative to canvas dimensions
 */
export function normalizePoint(point: Point, canvasWidth: number, canvasHeight: number): NormalizedPoint {
  return {
    x: point.x / canvasWidth,
    y: point.y / canvasHeight,
    pressure: point.pressure,
    tiltX: point.tiltX,
    tiltY: point.tiltY,
    timestamp: point.timestamp
  };
}

/**
 * Denormalize a point back to absolute coordinates
 */
export function denormalizePoint(point: NormalizedPoint, canvasWidth: number, canvasHeight: number): Point {
  // Ensure canvas dimensions are valid
  const safeWidth = Math.max(canvasWidth || 1920, 1);
  const safeHeight = Math.max(canvasHeight || 1080, 1);
  
  // Ensure point coordinates are valid numbers
  const x = isFinite(point.x) ? point.x : 0;
  const y = isFinite(point.y) ? point.y : 0;
  
  return {
    x: x * safeWidth,
    y: y * safeHeight,
    pressure: point.pressure,
    tiltX: point.tiltX,
    tiltY: point.tiltY,
    timestamp: point.timestamp
  };
}

/**
 * Normalize stroke width relative to canvas width
 * This ensures strokes appear proportionally the same across devices
 */
export function normalizeStrokeWidth(width: number, canvasWidth: number): number {
  // Ensure width is valid
  const safeWidth = isFinite(width) && width > 0 ? width : 2;
  
  // Don't normalize stroke width - keep it as absolute pixels
  // Stroke width should remain constant regardless of canvas size
  // This prevents strokes from becoming too thin or thick on different displays
  return safeWidth;
}

/**
 * Denormalize stroke width back to absolute pixels
 */
export function denormalizeStrokeWidth(normalizedWidth: number, canvasWidth: number): number {
  // Ensure normalized width is valid
  const safeWidth = isFinite(normalizedWidth) && normalizedWidth > 0 ? normalizedWidth : 2;
  
  // Check if this is an old recording with normalized stroke width (very small values)
  if (safeWidth < 0.1) {
    // This is likely an old normalized value (e.g., 0.00208 for 4px on 1920px canvas)
    // Convert it back using the old formula: width = normalizedWidth * canvasWidth
    const restoredWidth = safeWidth * (canvasWidth || 1920);
    return Math.min(Math.max(restoredWidth, 1), 100);
  }
  
  // For new recordings, stroke width is already in pixels
  // Ensure result is within reasonable bounds (minimum 1px, maximum 100px)
  return Math.min(Math.max(safeWidth, 1), 100);
}

/**
 * Normalize a stroke with all its points
 */
export function normalizeStroke(stroke: Stroke, canvasWidth: number, canvasHeight: number): NormalizedStroke {
  const normalizedStroke: NormalizedStroke = {
    id: stroke.id,
    points: stroke.points.map(p => normalizePoint(p, canvasWidth, canvasHeight)),
    color: stroke.color,
    width: normalizeStrokeWidth(stroke.width, canvasWidth),
    size: stroke.size ? normalizeStrokeWidth(stroke.size, canvasWidth) : undefined,
    opacity: stroke.opacity,
    tool: stroke.tool,
    timestamp: stroke.timestamp,
    isRecognizedShape: stroke.isRecognizedShape,
    originalShape: stroke.originalShape,
    confidence: stroke.confidence
  };

  if (stroke.transform) {
    normalizedStroke.transform = {
      left: stroke.transform.left / canvasWidth,
      top: stroke.transform.top / canvasHeight,
      scaleX: stroke.transform.scaleX,
      scaleY: stroke.transform.scaleY,
      angle: stroke.transform.angle
    };
  }

  // Add normalization flag directly to the object
  (normalizedStroke as any)._isNormalized = true;
  
  return normalizedStroke;
}

/**
 * Denormalize a stroke back to absolute coordinates
 */
export function denormalizeStroke(stroke: NormalizedStroke, canvasWidth: number, canvasHeight: number): Stroke {
  const denormalizedStroke: Stroke = {
    id: stroke.id,
    points: stroke.points.map(p => denormalizePoint(p, canvasWidth, canvasHeight)),
    color: stroke.color,
    width: denormalizeStrokeWidth(stroke.width, canvasWidth),
    size: stroke.size ? denormalizeStrokeWidth(stroke.size, canvasWidth) : undefined,
    opacity: stroke.opacity,
    tool: stroke.tool,
    timestamp: stroke.timestamp,
    isRecognizedShape: stroke.isRecognizedShape,
    originalShape: stroke.originalShape,
    confidence: stroke.confidence
  };

  if (stroke.transform) {
    denormalizedStroke.transform = {
      left: stroke.transform.left * canvasWidth,
      top: stroke.transform.top * canvasHeight,
      scaleX: stroke.transform.scaleX,
      scaleY: stroke.transform.scaleY,
      angle: stroke.transform.angle
    };
  }

  return denormalizedStroke;
}

/**
 * Normalize an array of strokes
 */
export function normalizeStrokes(strokes: Stroke[], canvasWidth: number, canvasHeight: number): NormalizedStroke[] {
  const normalized = strokes.map(stroke => normalizeStroke(stroke, canvasWidth, canvasHeight));
  // Add normalization flag to the array itself
  (normalized as any)._isNormalized = true;
  return normalized;
}

/**
 * Denormalize an array of strokes
 */
export function denormalizeStrokes(strokes: NormalizedStroke[], canvasWidth: number, canvasHeight: number): Stroke[] {
  return strokes.map(stroke => denormalizeStroke(stroke, canvasWidth, canvasHeight));
}

/**
 * Normalize fabric.js object coordinates
 */
export function normalizeFabricObject(obj: any, canvasWidth: number, canvasHeight: number): any {
  const normalized = { ...obj };
  
  // Normalize position
  if ('left' in obj) normalized.left = obj.left / canvasWidth;
  if ('top' in obj) normalized.top = obj.top / canvasHeight;
  
  // Normalize dimensions
  if ('width' in obj) normalized.width = obj.width / canvasWidth;
  if ('height' in obj) normalized.height = obj.height / canvasHeight;
  if ('radius' in obj) normalized.radius = obj.radius / canvasWidth;
  if ('rx' in obj) normalized.rx = obj.rx / canvasWidth;
  if ('ry' in obj) normalized.ry = obj.ry / canvasHeight;
  
  // Normalize stroke width
  if ('strokeWidth' in obj) normalized.strokeWidth = normalizeStrokeWidth(obj.strokeWidth, canvasWidth);
  
  // Normalize path points
  if (obj.type === 'path' && obj.path) {
    normalized.path = obj.path.map((cmd: any[]) => {
      const normalizedCmd = [...cmd];
      // Normalize x,y coordinates in path commands
      for (let i = 1; i < cmd.length; i += 2) {
        if (i < cmd.length) normalizedCmd[i] = cmd[i] / canvasWidth; // x
        if (i + 1 < cmd.length) normalizedCmd[i + 1] = cmd[i + 1] / canvasHeight; // y
      }
      return normalizedCmd;
    });
  }
  
  // Store original canvas dimensions for reference
  normalized._canvasWidth = canvasWidth;
  normalized._canvasHeight = canvasHeight;
  normalized._isNormalized = true;
  
  return normalized;
}

/**
 * Denormalize fabric.js object coordinates
 */
export function denormalizeFabricObject(obj: any, canvasWidth: number, canvasHeight: number): any {
  const denormalized = { ...obj };
  
  // Remove reference dimensions and normalization flag
  delete denormalized._canvasWidth;
  delete denormalized._canvasHeight;
  delete denormalized._isNormalized;
  
  // Denormalize position
  if ('left' in obj) denormalized.left = obj.left * canvasWidth;
  if ('top' in obj) denormalized.top = obj.top * canvasHeight;
  
  // Denormalize dimensions
  if ('width' in obj) denormalized.width = obj.width * canvasWidth;
  if ('height' in obj) denormalized.height = obj.height * canvasHeight;
  if ('radius' in obj) denormalized.radius = obj.radius * canvasWidth;
  if ('rx' in obj) denormalized.rx = obj.rx * canvasWidth;
  if ('ry' in obj) denormalized.ry = obj.ry * canvasHeight;
  
  // Denormalize stroke width
  if ('strokeWidth' in obj) denormalized.strokeWidth = denormalizeStrokeWidth(obj.strokeWidth, canvasWidth);
  
  // Denormalize path points
  if (obj.type === 'path' && obj.path) {
    denormalized.path = obj.path.map((cmd: any[]) => {
      const denormalizedCmd = [...cmd];
      // Denormalize x,y coordinates in path commands
      for (let i = 1; i < cmd.length; i += 2) {
        if (i < cmd.length) denormalizedCmd[i] = cmd[i] * canvasWidth; // x
        if (i + 1 < cmd.length) denormalizedCmd[i + 1] = cmd[i + 1] * canvasHeight; // y
      }
      return denormalizedCmd;
    });
  }
  
  return denormalized;
}

/**
 * Normalize recording event coordinates
 */
export function normalizeRecordingEvent(event: RecordingEvent, canvasWidth: number, canvasHeight: number): RecordingEvent {
  const normalized = { ...event };
  
  switch (event.type) {
    case 'stroke_start': {
      normalized.data = {
        ...event.data,
        point: normalizePoint(event.data.point, canvasWidth, canvasHeight),
        width: normalizeStrokeWidth(event.data.width, canvasWidth)
      };
      break;
    }
      
    case 'stroke_add': {
      normalized.data = {
        ...event.data,
        point: normalizePoint(event.data.point, canvasWidth, canvasHeight)
      };
      break;
    }
      
    case 'stroke_end': {
      if (event.data.shapePoints) {
        normalized.data = {
          ...event.data,
          shapePoints: event.data.shapePoints.map(p => normalizePoint(p, canvasWidth, canvasHeight))
        };
      }
      break;
    }
      
    case 'add_stroke': {
      normalized.data = {
        stroke: normalizeStroke(event.data.stroke, canvasWidth, canvasHeight)
      };
      break;
    }
      
    case 'width_change': {
      normalized.data = {
        width: normalizeStrokeWidth(event.data.width, canvasWidth)
      };
      break;
    }
      
    case 'object_move': {
      normalized.data = {
        ...event.data,
        from: {
          left: event.data.from.left / canvasWidth,
          top: event.data.from.top / canvasHeight
        },
        to: {
          left: event.data.to.left / canvasWidth,
          top: event.data.to.top / canvasHeight
        }
      };
      break;
    }
      
    case 'object_transform': {
      normalized.data = {
        ...event.data,
        transform: {
          ...event.data.transform,
          left: event.data.transform.left / canvasWidth,
          top: event.data.transform.top / canvasHeight
        }
      };
      break;
    }
      
    case 'area_erase_start':
    case 'area_erase_end': {
      const newData: any = { ...event.data };
      if ('startPoint' in event.data) {
        newData.startPoint = normalizePoint(event.data.startPoint, canvasWidth, canvasHeight);
      }
      if ('endPoint' in event.data) {
        newData.endPoint = normalizePoint(event.data.endPoint, canvasWidth, canvasHeight);
      }
      normalized.data = newData;
      break;
    }
      
    case 'area_erase_move':
    case 'laser_start':
    case 'laser_move':
    case 'pointer_move':
    case 'pointer_down':
    case 'pointer_up':
    case 'mouse_move': {
      if ('point' in event.data) {
        normalized.data = {
          ...event.data,
          point: normalizePoint(event.data.point, canvasWidth, canvasHeight)
        };
      }
      break;
    }
      
    case 'shape_preview_start': {
      normalized.data = {
        ...event.data,
        startPoint: normalizePoint(event.data.startPoint, canvasWidth, canvasHeight),
        width: normalizeStrokeWidth(event.data.width, canvasWidth)
      };
      break;
    }
      
    case 'shape_preview_update': {
      normalized.data = {
        endPoint: normalizePoint(event.data.endPoint, canvasWidth, canvasHeight)
      };
      break;
    }
      
    case 'stroke_move': {
      normalized.data = {
        ...event.data,
        deltaX: event.data.deltaX / canvasWidth,
        deltaY: event.data.deltaY / canvasHeight
      };
      break;
    }
      
    case 'question_resize': {
      normalized.data = {
        ...event.data,
        position: {
          left: event.data.position.left / canvasWidth,
          top: event.data.position.top / canvasHeight
        }
      };
      break;
    }
      
    case 'question_move': {
      normalized.data = {
        position: {
          left: event.data.position.left / canvasWidth,
          top: event.data.position.top / canvasHeight
        }
      };
      break;
    }
      
    case 'shape_preview': {
      const shapeEvent = event as any;
      normalized.data = {
        ...shapeEvent.data,
        startPoint: normalizePoint(shapeEvent.data.startPoint, canvasWidth, canvasHeight),
        endPoint: normalizePoint(shapeEvent.data.endPoint, canvasWidth, canvasHeight),
        points: shapeEvent.data.points.map((p: any) => normalizePoint(p, canvasWidth, canvasHeight))
      };
      break;
    }
  }
  
  return normalized;
}

/**
 * Denormalize recording event coordinates
 */
export function denormalizeRecordingEvent(event: RecordingEvent, canvasWidth: number, canvasHeight: number): RecordingEvent {
  const denormalized = { ...event };
  
  switch (event.type) {
    case 'stroke_start': {
      denormalized.data = {
        ...event.data,
        point: denormalizePoint(event.data.point as NormalizedPoint, canvasWidth, canvasHeight),
        width: denormalizeStrokeWidth(event.data.width, canvasWidth)
      };
      break;
    }
      
    case 'stroke_add': {
      denormalized.data = {
        ...event.data,
        point: denormalizePoint(event.data.point as NormalizedPoint, canvasWidth, canvasHeight)
      };
      break;
    }
      
    case 'stroke_end': {
      if (event.data.shapePoints) {
        denormalized.data = {
          ...event.data,
          shapePoints: event.data.shapePoints.map(p => 
            denormalizePoint(p as NormalizedPoint, canvasWidth, canvasHeight)
          )
        };
      }
      break;
    }
      
    case 'add_stroke': {
      denormalized.data = {
        stroke: denormalizeStroke(event.data.stroke as NormalizedStroke, canvasWidth, canvasHeight)
      };
      break;
    }
      
    case 'width_change': {
      denormalized.data = {
        width: denormalizeStrokeWidth(event.data.width, canvasWidth)
      };
      break;
    }
      
    case 'object_move': {
      denormalized.data = {
        ...event.data,
        from: {
          left: event.data.from.left * canvasWidth,
          top: event.data.from.top * canvasHeight
        },
        to: {
          left: event.data.to.left * canvasWidth,
          top: event.data.to.top * canvasHeight
        }
      };
      break;
    }
      
    case 'object_transform': {
      denormalized.data = {
        ...event.data,
        transform: {
          ...event.data.transform,
          left: event.data.transform.left * canvasWidth,
          top: event.data.transform.top * canvasHeight
        }
      };
      break;
    }
      
    case 'area_erase_start':
    case 'area_erase_end': {
      const newData: any = { ...event.data };
      if ('startPoint' in event.data) {
        newData.startPoint = denormalizePoint(event.data.startPoint as NormalizedPoint, canvasWidth, canvasHeight);
      }
      if ('endPoint' in event.data) {
        newData.endPoint = denormalizePoint(event.data.endPoint as NormalizedPoint, canvasWidth, canvasHeight);
      }
      denormalized.data = newData;
      break;
    }
      
    case 'area_erase_move':
    case 'laser_start':
    case 'laser_move':
    case 'pointer_move':
    case 'pointer_down':
    case 'pointer_up':
    case 'mouse_move': {
      if ('point' in event.data) {
        denormalized.data = {
          ...event.data,
          point: denormalizePoint(event.data.point as NormalizedPoint, canvasWidth, canvasHeight)
        };
      }
      break;
    }
      
    case 'shape_preview_start': {
      denormalized.data = {
        ...event.data,
        startPoint: denormalizePoint(event.data.startPoint as NormalizedPoint, canvasWidth, canvasHeight),
        width: denormalizeStrokeWidth(event.data.width, canvasWidth)
      };
      break;
    }
      
    case 'shape_preview_update': {
      denormalized.data = {
        endPoint: denormalizePoint(event.data.endPoint as NormalizedPoint, canvasWidth, canvasHeight)
      };
      break;
    }
      
    case 'stroke_move': {
      denormalized.data = {
        ...event.data,
        deltaX: event.data.deltaX * canvasWidth,
        deltaY: event.data.deltaY * canvasHeight
      };
      break;
    }
      
    case 'question_resize': {
      denormalized.data = {
        ...event.data,
        position: {
          left: event.data.position.left * canvasWidth,
          top: event.data.position.top * canvasHeight
        }
      };
      break;
    }
      
    case 'question_move': {
      denormalized.data = {
        position: {
          left: event.data.position.left * canvasWidth,
          top: event.data.position.top * canvasHeight
        }
      };
      break;
    }
      
    case 'shape_preview': {
      const shapeEvent = event as any;
      denormalized.data = {
        ...shapeEvent.data,
        startPoint: denormalizePoint(shapeEvent.data.startPoint as NormalizedPoint, canvasWidth, canvasHeight),
        endPoint: denormalizePoint(shapeEvent.data.endPoint as NormalizedPoint, canvasWidth, canvasHeight),
        points: shapeEvent.data.points.map((p: any) => 
          denormalizePoint(p as NormalizedPoint, canvasWidth, canvasHeight)
        )
      };
      break;
    }
  }
  
  return denormalized;
}

/**
 * Check if a stroke or array of strokes is normalized
 */
export function isNormalizedData(data: any): boolean {
  if (!data) return false;
  
  // Check if it's an array with normalization flag
  if (Array.isArray(data)) {
    // Check array flag
    if ((data as any)._isNormalized === true) return true;
    
    // Check if first item has flag (for backwards compatibility)
    if (data.length > 0 && (data[0] as any)?._isNormalized === true) return true;
    
    // Check if coordinates seem normalized (all between 0 and 1)
    if (data.length > 0 && data[0].points) {
      const firstStroke = data[0];
      const allPointsNormalized = firstStroke.points.every(
        (p: any) => p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1
      );
      return allPointsNormalized;
    }
  }
  
  // Check single stroke
  if (data._isNormalized === true) return true;
  
  // Check if points seem normalized
  if (data.points && Array.isArray(data.points)) {
    const allPointsNormalized = data.points.every(
      (p: any) => p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1
    );
    return allPointsNormalized;
  }
  
  return false;
}


/**
 * Normalize drawing data (fabric objects and strokes)
 */
export function normalizeDrawingData(
  drawingData: { fabricObjects: any[]; strokes: Stroke[] },
  canvasWidth: number,
  canvasHeight: number
): { fabricObjects: any[]; strokes: NormalizedStroke[] } {
  return {
    fabricObjects: drawingData.fabricObjects.map(obj => 
      normalizeFabricObject(obj, canvasWidth, canvasHeight)
    ),
    strokes: normalizeStrokes(drawingData.strokes, canvasWidth, canvasHeight)
  };
}

/**
 * Denormalize drawing data (fabric objects and strokes)
 */
export function denormalizeDrawingData(
  drawingData: { fabricObjects: any[]; strokes: NormalizedStroke[] },
  canvasWidth: number,
  canvasHeight: number
): { fabricObjects: any[]; strokes: Stroke[] } {
  return {
    fabricObjects: drawingData.fabricObjects.map(obj => 
      denormalizeFabricObject(obj, canvasWidth, canvasHeight)
    ),
    strokes: denormalizeStrokes(drawingData.strokes as NormalizedStroke[], canvasWidth, canvasHeight)
  };
}